// Electrical Node definitions — API-backed
// Nodes are round circle shapes representing substations / busbar connections
// They connect generation/storage/loads to transmission/distribution lines

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const authHeaders = () => {
  const token = localStorage.getItem('nrg-token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return headers;
};

// Version-tracked recommended engineering values
// Sources: pandapower/VDE, IEC 60076, CIGRE, IEEE C57.12, OFGEM loss studies
export const REFERENCE_VERSIONS = [
  {
    version: '1.0.0',
    date: '2026-04-03',
    source: 'pandapower std types, IEC 60076, CIGRE TB, OFGEM/Sohn loss study',
    values: {
      '11_11': { fixed_loss_pct: 0.0, variable_loss_pct: 0.005, vk_pct: null,
        notes: 'Busbar + switchgear only. No transformer. I²R from contacts (~50-200μΩ per device). Effectively zero in modeling.' },
      '11_33': { fixed_loss_pct: 0.07, variable_loss_pct: 0.60, vk_pct: 8.0,
        notes: 'MV/MV transformer 5-20 MVA class. P0≈0.07% (iron), Pk≈0.6% (copper). Ref: pandapower 25MVA scaled, IEC 60076.' },
      '33_33': { fixed_loss_pct: 0.0, variable_loss_pct: 0.003, vk_pct: null,
        notes: 'Switching station, busbars only. At 33kV currents are ~⅓ of 11kV for same power, I²R ~⅑. Negligible.' },
      '33_132': { fixed_loss_pct: 0.05, variable_loss_pct: 0.35, vk_pct: 12.5,
        notes: 'HV/MV transformer 20-90 MVA class. pandapower vkr=0.32-0.41%, P0=0.035-0.056%. Mid-range chosen.' },
      '132_132': { fixed_loss_pct: 0.0, variable_loss_pct: 0.002, vk_pct: null,
        notes: 'HV switching station. Very low currents relative to power. Many transmission models use zero.' },
      '132_400': { fixed_loss_pct: 0.035, variable_loss_pct: 0.25, vk_pct: 14.0,
        notes: 'EHV/HV transformer 200-500 MVA class. pandapower 160MVA 380/110: vkr=0.25%, Pfe=0.0375%. Largest units trend to 0.20%.' },
    },
  },
];

const latestRef = REFERENCE_VERSIONS[REFERENCE_VERSIONS.length - 1];

const makeConnectors = () => {
  const positions = [
    { id: 'n',  angle: 0,   label: 'N' },
    { id: 'ne', angle: 45,  label: 'NE' },
    { id: 'e',  angle: 90,  label: 'E' },
    { id: 'se', angle: 135, label: 'SE' },
    { id: 's',  angle: 180, label: 'S' },
    { id: 'sw', angle: 225, label: 'SW' },
    { id: 'w',  angle: 270, label: 'W' },
    { id: 'nw', angle: 315, label: 'NW' },
  ];
  return positions.map((p) => ({
    ...p,
    enabled: true,
    favorite: ['n', 'e', 's', 'w'].includes(p.id),
  }));
};

const defaultDefinitions = [
  {
    id: '11_11',
    name: '11/11 kV Busbar',
    category: 'electric',
    nodeType: 'busbar',
    hasTransformer: false,
    icon: '⬤',
    shape: { diameter: 80 },
    v_low: 11,
    v_high: 11,
    splitView: false,
    fillColor: '#F5C542',
    strokeColor: '#D4A843',
    connectors: makeConnectors(),
    maxSelectableConnectors: 8,
    fixed_loss_pct: latestRef.values['11_11'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['11_11'].variable_loss_pct,
    vk_pct: latestRef.values['11_11'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '10', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '525', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.0', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.005', unit: '%', required: true },
      { id: 'f6', name: 'busbars', label: 'Busbar Config', type: 'select', options: ['Single', 'Double', 'Ring'], defaultValue: 'Single', required: false },
      { id: 'f7', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
  {
    id: '11_33',
    name: '11/33 kV Substation',
    category: 'electric',
    nodeType: 'transformer',
    hasTransformer: true,
    icon: '◐',
    shape: { diameter: 100 },
    v_low: 11,
    v_high: 33,
    splitView: true,
    fillColor: '#F5C542',
    fillColorHigh: '#E8B828',
    strokeColor: '#D4A843',
    connectors: makeConnectors(),
    maxSelectableConnectors: 6,
    fixed_loss_pct: latestRef.values['11_33'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['11_33'].variable_loss_pct,
    vk_pct: latestRef.values['11_33'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '15', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '263', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.07', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.60', unit: '%', required: true },
      { id: 'f6', name: 'vk_pct', label: 'Impedance (vk)', type: 'number', defaultValue: '8.0', unit: '%', required: false },
      { id: 'f7', name: 'tap_range', label: 'Tap Range', type: 'text', defaultValue: '±10%', required: false },
      { id: 'f8', name: 'vector_group', label: 'Vector Group', type: 'select', options: ['Dyn11', 'Dyn1', 'YNd11', 'YNyn0', 'Dd0'], defaultValue: 'Dyn11', required: false },
      { id: 'f9', name: 'cooling', label: 'Cooling', type: 'select', options: ['ONAN', 'ONAF', 'OFAF', 'ODAF'], defaultValue: 'ONAN', required: false },
      { id: 'f10', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
  {
    id: '33_33',
    name: '33/33 kV Busbar',
    category: 'electric',
    nodeType: 'busbar',
    hasTransformer: false,
    icon: '⬤',
    shape: { diameter: 80 },
    v_low: 33,
    v_high: 33,
    splitView: false,
    fillColor: '#E8B828',
    strokeColor: '#C99A1E',
    connectors: makeConnectors(),
    maxSelectableConnectors: 8,
    fixed_loss_pct: latestRef.values['33_33'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['33_33'].variable_loss_pct,
    vk_pct: latestRef.values['33_33'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '40', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '700', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.0', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.003', unit: '%', required: true },
      { id: 'f6', name: 'busbars', label: 'Busbar Config', type: 'select', options: ['Single', 'Double', 'Ring'], defaultValue: 'Double', required: false },
      { id: 'f7', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
  {
    id: '33_132',
    name: '33/132 kV Substation',
    category: 'electric',
    nodeType: 'transformer',
    hasTransformer: true,
    icon: '◐',
    shape: { diameter: 110 },
    v_low: 33,
    v_high: 132,
    splitView: true,
    fillColor: '#E8B828',
    fillColorHigh: '#D4A020',
    strokeColor: '#C99A1E',
    connectors: makeConnectors(),
    maxSelectableConnectors: 6,
    fixed_loss_pct: latestRef.values['33_132'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['33_132'].variable_loss_pct,
    vk_pct: latestRef.values['33_132'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '50', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '219', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.05', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.35', unit: '%', required: true },
      { id: 'f6', name: 'vk_pct', label: 'Impedance (vk)', type: 'number', defaultValue: '12.5', unit: '%', required: false },
      { id: 'f7', name: 'tap_range', label: 'Tap Range', type: 'text', defaultValue: '±15%', required: false },
      { id: 'f8', name: 'vector_group', label: 'Vector Group', type: 'select', options: ['YNd11', 'YNyn0', 'Dyn11', 'YNa0'], defaultValue: 'YNd11', required: false },
      { id: 'f9', name: 'cooling', label: 'Cooling', type: 'select', options: ['ONAN', 'ONAF', 'OFAF', 'ODAF'], defaultValue: 'ONAF', required: false },
      { id: 'f10', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
  {
    id: '132_132',
    name: '132/132 kV Busbar',
    category: 'electric',
    nodeType: 'busbar',
    hasTransformer: false,
    icon: '⬤',
    shape: { diameter: 90 },
    v_low: 132,
    v_high: 132,
    splitView: false,
    fillColor: '#D4A020',
    strokeColor: '#B88A14',
    connectors: makeConnectors(),
    maxSelectableConnectors: 8,
    fixed_loss_pct: latestRef.values['132_132'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['132_132'].variable_loss_pct,
    vk_pct: latestRef.values['132_132'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '200', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '875', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.0', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.002', unit: '%', required: true },
      { id: 'f6', name: 'busbars', label: 'Busbar Config', type: 'select', options: ['Single', 'Double', 'Ring', 'Mesh'], defaultValue: 'Double', required: false },
      { id: 'f7', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
  {
    id: '132_400',
    name: '132/400 kV Substation',
    category: 'electric',
    nodeType: 'transformer',
    hasTransformer: true,
    icon: '◐',
    shape: { diameter: 120 },
    v_low: 132,
    v_high: 400,
    splitView: true,
    fillColor: '#D4A020',
    fillColorHigh: '#B88A14',
    strokeColor: '#A07A0E',
    connectors: makeConnectors(),
    maxSelectableConnectors: 6,
    fixed_loss_pct: latestRef.values['132_400'].fixed_loss_pct,
    variable_loss_pct: latestRef.values['132_400'].variable_loss_pct,
    vk_pct: latestRef.values['132_400'].vk_pct,
    refVersion: latestRef.version,
    overridden: false,
    fields: [
      { id: 'f1', name: 'name', label: 'Name', type: 'text', defaultValue: '', required: true },
      { id: 'f2', name: 's_rated', label: 'Rated Power', type: 'number', defaultValue: '300', unit: 'MVA', required: true },
      { id: 'f3', name: 'i_rated', label: 'Rated Current', type: 'number', defaultValue: '433', unit: 'A', required: true },
      { id: 'f4', name: 'fixed_loss_pct', label: 'Fixed Loss', type: 'number', defaultValue: '0.035', unit: '%', required: true },
      { id: 'f5', name: 'variable_loss_pct', label: 'Variable Loss', type: 'number', defaultValue: '0.25', unit: '%', required: true },
      { id: 'f6', name: 'vk_pct', label: 'Impedance (vk)', type: 'number', defaultValue: '14.0', unit: '%', required: false },
      { id: 'f7', name: 'tap_range', label: 'Tap Range', type: 'text', defaultValue: '±12%', required: false },
      { id: 'f8', name: 'vector_group', label: 'Vector Group', type: 'select', options: ['YNa0', 'YNyn0', 'YNd11'], defaultValue: 'YNa0', required: false },
      { id: 'f9', name: 'cooling', label: 'Cooling', type: 'select', options: ['ONAF', 'OFAF', 'ODAF'], defaultValue: 'OFAF', required: false },
      { id: 'f10', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
    ],
  },
];

// Merge API response {id, category, name, definition} into flat object
const mergeDefinition = (apiDef) => {
  const { definition, ...top } = apiDef;
  return { ...top, ...(definition || {}) };
};

// Split a flat definition into API shape {id, category, name, definition}
const splitDefinition = (def) => {
  const { id, category, name, ...rest } = def;
  return { id, category, name, definition: rest };
};

export async function loadNodeDefinitions() {
  try {
    const res = await fetch(`${API}/api/node-definitions`, { headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data.definitions || []);
      if (arr.length > 0) {
        return arr.map(mergeDefinition);
      }
    }
  } catch (err) {
    console.warn('Failed to load node definitions from API, using defaults:', err.message);
  }
  return [...defaultDefinitions];
}

export async function saveNodeDefinition(def) {
  const payload = splitDefinition(def);
  try {
    const res = await fetch(`${API}/api/node-definitions/${def.id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function updateNodeDefinition(id, updates) {
  try {
    const { id: _id, ...rest } = updates;
    const { category, name, ...defBody } = rest;
    const body = { definition: defBody };
    if (category !== undefined) body.category = category;
    if (name !== undefined) body.name = name;
    const res = await fetch(`${API}/api/node-definitions/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function createNodeDefinition(def) {
  try {
    const payload = splitDefinition(def);
    const res = await fetch(`${API}/api/node-definitions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteNodeDefinition(id) {
  try {
    const res = await fetch(`${API}/api/node-definitions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Calculate loss at a given load
export function calculateLoss(def, loadFraction) {
  const fixedLoss = def.fixed_loss_pct;
  const variableLoss = def.variable_loss_pct * Math.pow(loadFraction, 2);
  return {
    fixed: fixedLoss,
    variable: variableLoss,
    total: fixedLoss + variableLoss,
    formula: `${fixedLoss.toFixed(4)} + ${def.variable_loss_pct} × (I/I_rated)²`,
  };
}

// Get PyPSA parameters from node definition
export function toPyPSA(def) {
  if (!def.hasTransformer) {
    return {
      type: 'Bus',
      v_nom: def.v_low,
      link_efficiency: 1 - (def.variable_loss_pct / 100),
    };
  }
  return {
    type: 'Transformer',
    s_nom: null,
    v_hv: def.v_high,
    v_lv: def.v_low,
    r: def.variable_loss_pct / 100,
    x: def.vk_pct ? Math.sqrt(Math.pow(def.vk_pct / 100, 2) - Math.pow(def.variable_loss_pct / 100, 2)) : 0,
    g: def.fixed_loss_pct / 100,
    b: 0,
    tap_ratio: def.v_high / def.v_low,
  };
}
